// dashboard/src/pages/apps-setup/ShareTab.tsx
//
// Sharing one app outside the dashboard: the store screenshots that travel with
// it and the expiring links that serve the whole package — listing text in every
// locale, icon, screenshots, optionally one signed build — to someone with no
// account here. It used to sit at the bottom of the Build tab, which hid it from
// the ASO who produces most of what the package contains: the Build tab is
// developer-only. This tab is open to anyone who can see the app; attaching a
// build stays with developers, so an ASO issues a meta-only link.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Select } from '../../components/Select';
import { getApp, getApps } from '../../api/apps';
import { ApiError } from '../../api/client';
import {
  getStoreAssets, uploadStoreAsset, deleteStoreAsset, storeAssetUrl, getStoreListings,
  getHandoffLinks, createHandoffLink, revokeHandoffLink, repointHandoffLink,
  type IssuedHandoffLink, type StoreAsset, uploadFeatureGraphic,
  reorderStoreAssets, uploadStoreIcon, removeStoreIcon,
  getStoreScreenshotContext, planStoreScreenshots, generateStoreScreenshot, copyStoreScreenshots,
  generateStoreFeatureGraphic,
} from '../../api/app-handoff';
import { GenerateScreenshotsModal, type ShotTarget, type FeatureTarget } from '../../components/play/GenerateScreenshotsModal';
import { CopyScreenshotsBar } from '../../components/play/CopyScreenshotsBar';
import { getBuilds, type AppBuild } from '../../api/app-builds';
import { getPublisherOptions, getAppPublisher, setAppPublisher } from '../../api/publishers';
import { StoreListingEditor } from './StoreListingEditor';
import { prepareImageUpload, STORE_SCREENSHOT, PLAY_FEATURE_GRAPHIC, PLAY_ICON } from '../../lib/image-upload';
import { PLAY_LOCALES, playLocaleName } from '../../lib/playLocales';

/** The locale everything is authored in; matches SOURCE_LANG on the server. */
const SOURCE_LANG = 'en-US';
/** Play's cap per locale; the server enforces the same number. */
const MAX_PER_LOCALE = 8;

function fmtUnix(s: number): string {
  return new Date(s * 1000).toLocaleString();
}

function fmtUnixCompact(s: number): string {
  return new Date(s * 1000).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function ShareTab({ canAttachBuild }: { canAttachBuild: boolean }) {
  const appsQ = useQuery({ queryKey: ['apps'], queryFn: getApps });
  // ?tab=share&app=<id> opens the tab on an app — App Factory links here.
  const [searchParams] = useSearchParams();
  const [appId, setAppId] = useState(searchParams.get('app') ?? '');
  const appOptions = useMemo(
    () => (appsQ.data ?? []).map((app) => ({ value: app.id, label: `${app.name} · ${app.bundle_id}` })),
    [appsQ.data],
  );

  return (
    <div className="space-y-4">
      <section className="bg-surface border border-border rounded-lg p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-medium text-content">Share an app</h2>
            <p className="mt-1 text-sm text-content-subtle">
              One expiring link with the listing text in every locale, the icon, the screenshots
              {canAttachBuild ? ' and a signed build' : ''} — no login on the other side.
              The signing key is never part of it.
            </p>
          </div>
          <Select
            value={appId}
            options={appOptions}
            onChange={setAppId}
            placeholder={appsQ.isLoading ? 'Loading…' : '— select an app —'}
            searchable
            disabled={appsQ.isLoading}
            className="w-full sm:w-[24rem]"
          />
        </div>
      </section>

      {appId ? (
        <HandoffPanel key={appId} appId={appId} canAttachBuild={canAttachBuild} />
      ) : (
        <section className="flex min-h-64 items-center justify-center rounded-lg border border-dashed border-border bg-surface/40 p-8 text-center">
          <div className="max-w-sm">
            <p className="font-medium text-content">Choose an app to share</p>
            <p className="mt-1 text-sm text-content-subtle">
              Its store screenshots and the links issued for it will appear here.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * The screenshots that travel with one app and the links that serve its package.
 * `canAttachBuild` mirrors the server: only a technical admin may pin a build to
 * a link, and only they can list this app's builds in the first place.
 */
export function HandoffPanel({ appId, canAttachBuild }: { appId: string; canAttachBuild: boolean }) {
  const qc = useQueryClient();
  const linksQ = useQuery({ queryKey: ['handoff-links', appId], queryFn: () => getHandoffLinks(appId) });
  const buildsQ = useQuery({
    queryKey: ['app-builds', appId],
    queryFn: () => getBuilds(appId),
    enabled: canAttachBuild,
  });

  const [buildId, setBuildId] = useState('');
  const [ttl, setTtl] = useState(String(24 * 7));
  const [note, setNote] = useState('');
  const [issued, setIssued] = useState<IssuedHandoffLink | null>(null);
  const [copied, setCopied] = useState(false);

  // Who will host the app. The dropdown pre-fills the publisher's default
  // prices; what is typed here is what gets frozen on the app.
  const publishersQ = useQuery({ queryKey: ['publisher-options'], queryFn: getPublisherOptions });
  const appPubQ = useQuery({ queryKey: ['app-publisher', appId], queryFn: () => getAppPublisher(appId) });
  const [publisherId, setPublisherId] = useState('');
  const [publishUsd, setPublishUsd] = useState('');
  const [rentUsd, setRentUsd] = useState('');
  const [pubTouched, setPubTouched] = useState(false);
  useEffect(() => {
    // Seed the form from the app's current assignment once it is known.
    if (pubTouched || !appPubQ.data) return;
    if (appPubQ.data.publisher_id) {
      setPublisherId(appPubQ.data.publisher_id);
      setPublishUsd(String((appPubQ.data.publish_cents ?? 0) / 100));
      setRentUsd(String((appPubQ.data.rent_cents ?? 0) / 100));
    }
  }, [appPubQ.data, pubTouched]);
  function choosePublisher(id: string) {
    setPubTouched(true);
    setPublisherId(id);
    const p = (publishersQ.data ?? []).find((x) => x.id === id);
    if (p) {
      setPublishUsd(String(p.publish_cents / 100));
      setRentUsd(String(p.rent_cents / 100));
    }
  }
  const toCents = (v: string) => Math.round(Number(v || 0) * 100);
  const savePublisher = useMutation({
    mutationFn: () => setAppPublisher(appId, {
      publisher_id: publisherId || null,
      publish_cents: toCents(publishUsd), rent_cents: toCents(rentUsd),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-publisher', appId] }),
  });

  // Only builds that actually produced an artifact can be handed over.
  const downloadable = useMemo(
    () => (buildsQ.data ?? []).filter((b: AppBuild) => b.artifact_r2_key),
    [buildsQ.data],
  );
  useEffect(() => {
    if (!buildId && downloadable[0]) setBuildId(downloadable[0].id);
  }, [downloadable, buildId]);

  const create = useMutation({
    mutationFn: () => createHandoffLink({
      app_id: appId,
      build_id: canAttachBuild ? (buildId || undefined) : undefined,
      ttl_hours: Number(ttl),
      note: note.trim() || undefined,
      publisher_id: publisherId || undefined,
      publish_cents: publisherId ? toCents(publishUsd) : undefined,
      rent_cents: publisherId ? toCents(rentUsd) : undefined,
    }),
    onSuccess: (link) => {
      setIssued(link);
      setCopied(false);
      setNote('');
      qc.invalidateQueries({ queryKey: ['handoff-links', appId] });
      qc.invalidateQueries({ queryKey: ['app-publisher', appId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeHandoffLink(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['handoff-links', appId] }),
  });

  // Repointing a live link at the build selected above: the receiver keeps the
  // URL they already have and gets the rebuilt app from it. Everything else in
  // the package is read live from the app, so only the build ever goes stale.
  const repoint = useMutation({
    mutationFn: (id: string) => repointHandoffLink(id, buildId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['handoff-links', appId] }),
  });

  async function copyLink() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied — the URL is on screen and selectable.
    }
  }

  const links = linksQ.data ?? [];
  const nowSec = Math.floor(Date.now() / 1000);

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
      {/* Listing text the package is built from */}
      <StoreListingEditor appId={appId} />

      {/* The icon, replaceable here so a wizard miss is fixed before anyone sees it */}
      <div className="border-t border-border pt-3">
        <StoreIcon appId={appId} />
      </div>

      {/* Screenshots that travel with the app */}
      <div className="border-t border-border pt-3">
        <StoreScreenshots appId={appId} />
      </div>

      {/* Who hosts it, and for how much */}
      <div className="space-y-2 border-t border-border pt-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[14rem] flex-1">
            <label className="block text-xs text-content-muted mb-1">Publisher (who uploads to their console)</label>
            <Select
              value={publisherId}
              options={(publishersQ.data ?? []).map((p) => ({
                value: p.id, label: p.has_chat ? p.name : `${p.name} (no Telegram chat)`,
              }))}
              onChange={choosePublisher}
              placeholder="— none, just a link —"
              className="w-full"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs text-content-muted mb-1">$ per publish</label>
            <input
              type="number" min="0" step="1" value={publishUsd} disabled={!publisherId}
              onChange={(e) => { setPubTouched(true); setPublishUsd(e.target.value); }}
              className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content disabled:opacity-40"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs text-content-muted mb-1">$ rent / week</label>
            <input
              type="number" min="0" step="1" value={rentUsd} disabled={!publisherId}
              onChange={(e) => { setPubTouched(true); setRentUsd(e.target.value); }}
              className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content disabled:opacity-40"
            />
          </div>
          {appPubQ.data && (pubTouched || !!appPubQ.data.publisher_id) && (
            <button
              type="button"
              disabled={savePublisher.isPending}
              onClick={() => savePublisher.mutate()}
              className="px-3 py-2 text-sm rounded-md border border-border text-content hover:bg-surface-muted disabled:opacity-40"
              title="Save the publisher and prices without issuing a new link"
            >
              {savePublisher.isPending ? 'Saving…' : 'Save publisher'}
            </button>
          )}
        </div>
        {appPubQ.data?.publisher_id && (
          <p className="text-xs text-content-subtle">
            Hosted by <span className="text-content">{appPubQ.data.publisher_name}</span>
            {appPubQ.data.published_at
              ? ` · published ${appPubQ.data.published_at} (fee booked)`
              : appPubQ.data.released_at ? ' · live in the store' : ' · not in the store yet'}
            {appPubQ.data.listing_status && ` · listing check: ${appPubQ.data.listing_status}`}
            {appPubQ.data.rent_from_week && ` · rent rate since ${appPubQ.data.rent_from_week}`}
            {' · '}
            <a href="/finance/publishers" className="text-primary hover:underline">weekly bill</a>
            {appPubQ.data.released_at && ' · a new rent applies from the current week on, earlier weeks keep theirs'}
          </p>
        )}
        {savePublisher.error && <p className="text-red-500 text-sm">{(savePublisher.error as ApiError).message}</p>}
        <p className="text-xs text-content-subtle">
          With a publisher chosen, creating the link posts it to their Telegram chat, and the app is billed when it appears in the store.
        </p>
      </div>

      {/* Issue a link */}
      <div className="space-y-2 border-t border-border pt-3">
        <div className="flex flex-wrap items-end gap-2">
          {canAttachBuild && (
            <div className="min-w-[16rem] flex-1">
              <label className="block text-xs text-content-muted mb-1">Build in the package</label>
              <Select
                value={buildId}
                options={downloadable.map((b: AppBuild) => ({
                  value: b.id,
                  label: `${b.build_type.toUpperCase()} ${b.version_name} (${b.version_code}) · ${fmtUnixCompact(b.created_at)}`,
                }))}
                onChange={setBuildId}
                placeholder={downloadable.length === 0 ? '— no downloadable build —' : '— meta only —'}
                className="w-full"
              />
            </div>
          )}
          <div>
            <label className="block text-xs text-content-muted mb-1">Valid for</label>
            <select
              value={ttl}
              onChange={(e) => setTtl(e.target.value)}
              className="px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
            >
              <option value="24">24 hours</option>
              <option value="72">3 days</option>
              <option value={String(24 * 7)}>7 days</option>
              <option value={String(24 * 30)}>30 days</option>
            </select>
          </div>
          <button
            type="button"
            disabled={create.isPending}
            onClick={() => create.mutate()}
            className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
          >
            {create.isPending ? 'Creating…' : 'Create link'}
          </button>
          {!canAttachBuild && (
            <p className="text-xs text-content-subtle">
              Listing, icon and screenshots. A build can only be attached by a developer, on the Build tab.
            </p>
          )}
        </div>
        <input
          value={note}
          placeholder="Note shown on the page (optional) — e.g. upload to console X, internal testing track"
          onChange={(e) => setNote(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
        />
        {create.error && <p className="text-red-500 text-sm">{(create.error as ApiError).message}</p>}
        {repoint.error && <p className="text-red-500 text-sm">{(repoint.error as ApiError).message}</p>}

        {issued && (
          <div className="rounded-md border border-primary bg-surface-muted p-3">
            <p className="text-xs text-content-muted">
              Copy it now — the token is not stored and cannot be shown again.
            </p>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 min-w-0 break-all text-xs text-content">{issued.url}</code>
              <button type="button" onClick={copyLink} className="shrink-0 text-xs text-primary hover:underline">
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-1 text-xs text-content-subtle">Expires {fmtUnix(issued.expires_at)}</p>
          </div>
        )}
      </div>

      {/* What has been issued so far */}
      {links.length > 0 && (
        <div className="border-t border-border pt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle">
                <th className="py-1 pr-2 font-normal">Created</th>
                <th className="py-1 pr-2 font-normal">By</th>
                <th className="py-1 pr-2 font-normal">Publisher</th>
                <th className="py-1 pr-2 font-normal">Build</th>
                <th className="py-1 pr-2 font-normal">State</th>
                <th className="py-1 pr-2 font-normal">Opens / files</th>
                <th className="py-1 font-normal" />
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const dead = l.revoked_at !== null || l.expires_at <= nowSec;
                return (
                  <tr key={l.id} className="border-t border-border">
                    <td className="py-1.5 pr-2 text-content-muted">{fmtUnixCompact(l.created_at)}</td>
                    <td className="py-1.5 pr-2 text-content-muted">{l.created_by_name ?? '—'}</td>
                    <td className="py-1.5 pr-2 text-content-muted">{l.publisher_name ?? '—'}</td>
                    <td className="py-1.5 pr-2 text-content-muted">
                      {l.build_type ? `${l.build_type.toUpperCase()} ${l.version_name} (${l.version_code})` : '—'}
                    </td>
                    <td className="py-1.5 pr-2">
                      <span className={dead ? 'text-content-subtle' : 'text-green-400'}>
                        {l.revoked_at !== null
                          ? 'revoked'
                          : l.expires_at <= nowSec ? 'expired' : `until ${fmtUnixCompact(l.expires_at)}`}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-content-muted">{l.view_count} / {l.download_count}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      {!dead && (
                        <>
                          {canAttachBuild && buildId && l.build_id !== buildId && (
                            <button
                              type="button"
                              disabled={repoint.isPending}
                              onClick={() => repoint.mutate(l.id)}
                              title="Serve the build selected above from this same link"
                              className="mr-3 text-xs text-content-muted hover:text-primary disabled:opacity-40"
                            >
                              Use selected build
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={revoke.isPending}
                            onClick={() => revoke.mutate(l.id)}
                            className="text-xs text-content-muted hover:text-red-500 disabled:opacity-40"
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * The app icon as the package serves it. The wizard sets it at creation; this
 * is where it gets swapped when the drawn one misses, before a link is issued
 * or the listing goes to Play. The file is fitted to 512×512 client-side, so
 * a 1024 wizard export or a stray JPEG both land in Play's shape.
 */
function StoreIcon({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const appQ = useQuery({ queryKey: ['app', appId], queryFn: () => getApp(appId) });
  const [error, setError] = useState('');

  // Both the Apps list and this card read icon_url, so both refresh.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['app', appId] });
    qc.invalidateQueries({ queryKey: ['apps'] });
  };
  const upload = useMutation({
    mutationFn: async (file: File) => uploadStoreIcon(appId, await prepareImageUpload(file, PLAY_ICON)),
    onSuccess: refresh,
    onError: (e) => setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Upload failed'),
  });
  const remove = useMutation({
    mutationFn: () => removeStoreIcon(appId),
    onSuccess: refresh,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Remove failed'),
  });

  const iconUrl = appQ.data?.icon_url || '';
  const busy = upload.isPending || remove.isPending;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-content">Icon</h4>
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/40 p-3">
        <div className="flex items-center gap-3 min-w-0">
          {iconUrl
            ? <img src={iconUrl} alt="" className="h-12 w-12 rounded-lg border border-border flex-none object-cover" />
            : <span className="h-12 w-12 rounded-lg border border-dashed border-border flex-none" />}
          <div className="text-xs text-content-muted min-w-0">
            <p className="text-content">App icon</p>
            <p>{iconUrl ? '512×512, replaces on upload.' : 'None yet — the handoff page and Play both need one.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-none">
          <label className="text-xs cursor-pointer text-primary hover:underline">
            {upload.isPending ? 'Uploading…' : iconUrl ? 'Replace' : 'Upload 512×512'}
            <input type="file" accept="image/*" className="hidden" disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { setError(''); upload.mutate(f); } }} />
          </label>
          {iconUrl && (
            <button type="button" className="text-xs text-content-muted hover:text-red-400" disabled={busy}
              onClick={() => { setError(''); remove.mutate(); }}>Remove</button>
          )}
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
}

/**
 * The screenshots that travel with the app, one set per locale. Every locale
 * keeps its own frames: the handoff page and the Play publish step show a
 * locale only what was uploaded for it, never the source locale's set. The
 * chips are the listing's locales plus any locale that already has frames, so
 * a locale can get its screenshots before (or without) its text.
 */
function StoreScreenshots({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const assetsQ = useQuery({ queryKey: ['store-assets', appId], queryFn: () => getStoreAssets(appId) });
  const listingsQ = useQuery({ queryKey: ['store-listings', appId], queryFn: () => getStoreListings(appId) });

  const [lang, setLang] = useState('');
  const [added, setAdded] = useState<string[]>([]);
  const [addLang, setAddLang] = useState('');
  const [uploadError, setUploadError] = useState('');

  const assets = useMemo(() => (assetsQ.data ?? []).filter((a) => a.kind === 'screenshot'), [assetsQ.data]);
  const feature = useMemo(() => (assetsQ.data ?? []).find((a) => a.kind === 'feature') ?? null, [assetsQ.data]);
  const uploadFeature = useMutation({
    mutationFn: async (file: File) => uploadFeatureGraphic(appId, await prepareImageUpload(file, PLAY_FEATURE_GRAPHIC)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store-assets', appId] }),
    onError: (e) => setUploadError(e instanceof ApiError ? e.message : 'Upload failed'),
  });

  // Source first, then the rest alphabetically — the same order the package uses.
  const locales = useMemo(() => {
    const set = new Set<string>([SOURCE_LANG]);
    for (const l of listingsQ.data ?? []) set.add(l.lang);
    for (const a of assets) set.add(a.lang);
    for (const l of added) set.add(l);
    return Array.from(set).sort((a, b) => (a === SOURCE_LANG ? -1 : b === SOURCE_LANG ? 1 : a.localeCompare(b)));
  }, [listingsQ.data, assets, added]);

  const activeLang = locales.includes(lang) ? lang : SOURCE_LANG;
  const shots = assets.filter((a) => a.lang === activeLang);
  const countOf = (code: string) => assets.filter((a) => a.lang === code).length;

  const upload = useMutation({
    mutationFn: async ({ file, target }: { file: File; target: string }) =>
      uploadStoreAsset(appId, await prepareImageUpload(file, STORE_SCREENSHOT), target),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store-assets', appId] }),
    onError: (e) => setUploadError(e instanceof ApiError ? e.message : 'Upload failed'),
  });

  const removeAsset = useMutation({
    mutationFn: (assetId: string) => deleteStoreAsset(appId, assetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store-assets', appId] }),
    onError: (e) => setUploadError(e instanceof ApiError ? e.message : 'Remove failed'),
  });

  // Swapping a frame with its neighbour: the whole locale's order is sent, so
  // two quick clicks can never leave two frames on the same slot.
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderStoreAssets(appId, activeLang, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store-assets', appId] }),
    onError: (e) => setUploadError(e instanceof ApiError ? e.message : 'Reorder failed'),
  });
  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= shots.length) return;
    const ids = shots.map((a) => a.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    setUploadError('');
    reorder.mutate(ids);
  }
  const shuffling = reorder.isPending || removeAsset.isPending;

  const addOptions = useMemo(
    () => PLAY_LOCALES.filter((l) => !locales.includes(l.code))
      .map((l) => ({ value: l.code, label: `${l.name} · ${l.code}` })),
    [locales],
  );

  function addLocale(code: string) {
    if (!code) return;
    setAddLang('');
    setUploadError('');
    setAdded((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setLang(code);
  }

  // Several files at once land in the order they were picked, one request each,
  // so a failure names the locale and leaves the earlier ones in place.
  async function uploadFiles(files: File[]) {
    if (files.length === 0) return;
    setUploadError('');
    const room = MAX_PER_LOCALE - shots.length;
    const picked = files.slice(0, Math.max(room, 0));
    if (picked.length === 0) {
      setUploadError(`At most ${MAX_PER_LOCALE} screenshots per locale (${activeLang} is full)`);
      return;
    }
    for (const file of picked) {
      try {
        await upload.mutateAsync({ file, target: activeLang });
      } catch {
        return; // onError already showed the message
      }
    }
  }

  const full = shots.length >= MAX_PER_LOCALE;

  // AI frames for this locale, drawn with the app's brand pack like the
  // wizard's; the server attaches each one as it is drawn.
  const [showGenerate, setShowGenerate] = useState(false);
  // The package has one feature graphic, drawn from the source listing: it
  // rides along with a set generated for the source locale, and has its own
  // AI button on the card. The server attaches it, replacing the current one.
  const featureTarget = useMemo<FeatureTarget>(() => ({
    exists: !!feature,
    draw: async (input) => {
      const out = await generateStoreFeatureGraphic(appId, input);
      return { url: storeAssetUrl(out.media_key), tagline: out.tagline, scene: out.scene };
    },
  }), [appId, feature]);
  const drawFeature = useMutation({
    mutationFn: () => featureTarget.draw({ use_brand: true, ui_refs: false }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store-assets', appId] }),
    onError: (e) => setUploadError(e instanceof Error ? e.message : 'Banner generation failed'),
  });

  const shotTarget = useMemo<ShotTarget>(() => ({
    lang: activeLang,
    existingCount: shots.length,
    canReplace: false,
    noun: 'package',
    contextKey: ['store-shot-context', appId],
    context: () => getStoreScreenshotContext(appId),
    plan: (input) => planStoreScreenshots(appId, input),
    draw: async (input) => {
      const out = await generateStoreScreenshot(appId, input);
      return { url: storeAssetUrl(out.media_key), key: out.media_key, contentType: out.contentType };
    },
    ...(activeLang === SOURCE_LANG ? { feature: featureTarget } : {}),
  }), [appId, activeLang, shots.length, featureTarget]);

  // The copy bar: locales with frames to pull from, and how many locales
  // "all" writes to — the server's list is the listing's locales plus any
  // with frames, never ones only added in this browser.
  const copySources = locales.filter((l) => l !== activeLang && countOf(l) > 0).map((l) => ({ lang: l, count: countOf(l) }));
  const serverLocales = new Set<string>([SOURCE_LANG, ...(listingsQ.data ?? []).map((l) => l.lang), ...assets.map((a) => a.lang)]);
  serverLocales.delete(activeLang);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/40 p-3">
        <div className="flex items-center gap-3 min-w-0">
          {feature
            ? <img src={storeAssetUrl(feature.media_key)} alt="" className="h-12 w-auto rounded border border-border flex-none" />
            : <span className="h-12 w-24 rounded border border-dashed border-border flex-none" />}
          <div className="text-xs text-content-muted min-w-0">
            <p className="text-sm font-medium text-content">Feature graphic</p>
            <p>{feature ? '1024×500, replaces on upload.' : 'None yet — Play will not review a release without one.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-none">
          <button type="button" className="text-xs text-primary hover:underline disabled:text-content-subtle disabled:no-underline"
            disabled={drawFeature.isPending || uploadFeature.isPending}
            title="Drawn with the app's brand pack from the source listing, by Play's feature-graphic rules"
            onClick={() => { setUploadError(''); drawFeature.mutate(); }}>
            {drawFeature.isPending ? 'Drawing…' : feature ? 'Redraw with AI' : 'Draw with AI'}
          </button>
          <label className="text-xs cursor-pointer text-primary hover:underline">
            {uploadFeature.isPending ? 'Uploading…' : feature ? 'Replace' : 'Upload 1024×500'}
            <input type="file" accept="image/*" className="hidden" disabled={uploadFeature.isPending}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { setUploadError(''); uploadFeature.mutate(f); } }} />
          </label>
          {feature && (
            <button type="button" className="text-xs text-content-muted hover:text-red-400" disabled={removeAsset.isPending}
              onClick={() => { setUploadError(''); removeAsset.mutate(feature.id); }}>Remove</button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-content">
          Store screenshots ({assets.length} in {locales.filter((l) => countOf(l) > 0).length} locale(s))
        </h4>
        <div className="w-56">
          <Select
            value={addLang}
            options={addOptions}
            onChange={addLocale}
            placeholder="+ Add locale"
            searchable
            className="w-full"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {locales.map((code) => {
          const n = countOf(code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => { setUploadError(''); setLang(code); }}
              title={playLocaleName(code) ?? code}
              className={`rounded-md border px-2 py-1 text-xs ${
                code === activeLang
                  ? 'border-primary bg-primary text-white'
                  : 'border-border bg-background text-content-muted hover:text-content'
              }`}
            >
              {code}
              {code === SOURCE_LANG ? ' · source' : ''}
              {n > 0 ? ` · ${n}` : ''}
            </button>
          );
        })}
      </div>

      <div className="space-y-2 rounded-md border border-border bg-background/40 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-content-muted">
            {playLocaleName(activeLang) ?? activeLang} · {shots.length}/{MAX_PER_LOCALE}
          </span>
          <label className={`text-xs cursor-pointer ${full ? 'text-content-subtle' : 'text-primary hover:underline'}`}>
            {upload.isPending ? 'Uploading…' : `Upload to ${activeLang}`}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              disabled={upload.isPending || full}
              onChange={(e) => {
                // Copy the File objects out before resetting the input: the
                // reset empties the live FileList, the Files themselves survive.
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                void uploadFiles(files);
              }}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={full}
            onClick={() => { setUploadError(''); setShowGenerate(true); }}
            title={full ? `${activeLang} is full — remove frames first` : 'Draw frames with the app\'s brand pack'}
            className="text-xs text-primary hover:underline disabled:text-content-subtle disabled:no-underline"
          >
            Generate screenshots (AI)
          </button>
        </div>
        <CopyScreenshotsBar
          lang={activeLang}
          sources={copySources}
          ownCount={shots.length}
          otherLocales={serverLocales.size}
          defaultMode="append"
          onCopy={(input) => copyStoreScreenshots(appId, input)}
          onDone={() => qc.invalidateQueries({ queryKey: ['store-assets', appId] })}
        />
        <GenerateScreenshotsModal
          isOpen={showGenerate}
          target={shotTarget}
          onClose={() => setShowGenerate(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ['store-assets', appId] })}
        />
        {shots.length === 0 ? (
          <p className="text-xs text-content-subtle">
            {activeLang === SOURCE_LANG
              ? 'None yet. Frames drawn on the Create tab are saved here automatically; older apps need an upload.'
              : `No screenshots for ${activeLang}. Without its own set the handoff page shows none for this locale, and Play falls back to the default language.`}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {shots.map((a: StoreAsset, i) => (
              <div key={a.id} className="w-20">
                <img src={storeAssetUrl(a.media_key)} alt="" className="w-20 rounded border border-border object-cover" />
                <div className="mt-1 flex items-center justify-between gap-1">
                  <span className="text-[10px] text-content-subtle truncate" title={a.caption ?? undefined}>{i + 1}</span>
                  <span className="flex items-center gap-0.5">
                    <button
                      type="button"
                      disabled={shuffling || i === 0}
                      onClick={() => move(i, -1)}
                      title="Move left"
                      className="px-1 text-xs text-content-muted hover:text-content disabled:opacity-30"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      disabled={shuffling || i === shots.length - 1}
                      onClick={() => move(i, 1)}
                      title="Move right"
                      className="px-1 text-xs text-content-muted hover:text-content disabled:opacity-30"
                    >
                      →
                    </button>
                  </span>
                  <button
                    type="button"
                    disabled={shuffling}
                    onClick={() => { setUploadError(''); removeAsset.mutate(a.id); }}
                    className="text-xs text-content-muted hover:text-red-500 shrink-0 disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {uploadError && <p className="text-red-500 text-xs">{uploadError}</p>}
      </div>
    </div>
  );
}
